import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Switch, TextInput, Badge, Checkbox, NumberInput, Select, Button } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faFloppyDisk } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { zReportEmailRecipientsSchema } from 'shared-models/browser';
import { QuickNotesField } from '../category-manager/quick-notes/QuickNotesField';
import { matchesSearchQuery, normalizeSearchText } from '../../../../../utils/semantic-search.util';
import classes from './pos-settings-modal.module.scss';

const collectText = (node: React.ReactNode, sink: string[]): void => {
  if (typeof node === 'string' || typeof node === 'number') {
    sink.push(String(node));
    return;
  }
  if (Array.isArray(node)) {
    node.forEach((child) => collectText(child, sink));
    return;
  }
  if (React.isValidElement(node)) {
    collectText((node.props as { children?: React.ReactNode }).children, sink);
  }
};

// Rows stay plain JSX: their own visible label and hint text are the search index,
// so a new setting becomes searchable without registering keywords anywhere.
const FilteredSettingsList: React.FC<{ query: string; children: React.ReactNode }> = ({ query, children }) => {
  const rows = React.Children.toArray(children);
  const visible = query === ''
    ? rows
    : rows.filter((row) => {
      const text: string[] = [];
      collectText(row, text);
      return matchesSearchQuery(query, text.map(normalizeSearchText));
    });

  return (
    <>
      <div className={classes.list}>{visible}</div>
      {query !== '' && visible.length === 0 && (
        <p className={classes.searchEmpty} data-testid="pos-settings-search-empty">לא נמצאו תוצאות</p>
      )}
    </>
  );
};

type DrawerEmployeeOption = {
  id: number;
  fullName: string;
};

type PosSettingsModalProps = {
  opened: boolean;
  onClose: () => void;
  viewportWidth: number;
  isSubmitting: boolean;
  isSuperAdmin: boolean;
  requiresManagerApproval: boolean;
  onChangeRequiresManagerApproval: (next: boolean) => void;
  cancelRequiresManagerApproval?: boolean;
  onChangeCancelRequiresManagerApproval?: (next: boolean) => void;
  globalDiscountRequiresManagerApproval?: boolean;
  onChangeGlobalDiscountRequiresManagerApproval?: (next: boolean) => void;
  itemDiscountRequiresManagerApproval?: boolean;
  onChangeItemDiscountRequiresManagerApproval?: (next: boolean) => void;
  onTheHouseRequiresManagerApproval?: boolean;
  onChangeOnTheHouseRequiresManagerApproval?: (next: boolean) => void;
  transferRequiresManagerApproval?: boolean;
  onChangeTransferRequiresManagerApproval?: (next: boolean) => void;
  mergeRequiresManagerApproval?: boolean;
  onChangeMergeRequiresManagerApproval?: (next: boolean) => void;
  manualMergedTableSplitRequiresManagerApproval?: boolean;
  onChangeManualMergedTableSplitRequiresManagerApproval?: (next: boolean) => void;
  shiftCloseCashBalancesEnabled?: boolean;
  onChangeShiftCloseCashBalancesEnabled?: (next: boolean) => void;
  excludeOpeningCashFromDrawer?: boolean;
  onChangeExcludeOpeningCashFromDrawer?: (next: boolean) => void;
  ordersPageWaiterOwnVisibility: boolean;
  onChangeOrdersPageWaiterOwnVisibility: (next: boolean) => void;
  kitchenBonTableNumberAtBottom: boolean;
  onChangeKitchenBonTableNumberAtBottom: (next: boolean) => void;
  stayInOrderAfterFire: boolean;
  onChangeStayInOrderAfterFire: (next: boolean) => void;
  employeeIdleLogoutMinutes?: number;
  onChangeEmployeeIdleLogoutMinutes?: (next: number) => void;
  warnNoBillBeforePayment?: boolean;
  onChangeWarnNoBillBeforePayment?: (next: boolean) => void;
  hideGeneralItemInMenu: boolean;
  onChangeHideGeneralItemInMenu: (next: boolean) => void;
  disableCashTips: boolean;
  onChangeDisableCashTips: (next: boolean) => void;
  waiterOwnOrderEntryOnly?: boolean;
  onChangeWaiterOwnOrderEntryOnly?: (next: boolean) => void;
  singleWorkerPerTable?: boolean;
  onChangeSingleWorkerPerTable?: (next: boolean) => void;
  manualCreditEntryBlocked: boolean;
  onChangeManualCreditEntryBlocked: (next: boolean) => void;
  customerNameOnOrderEntry: boolean;
  onChangeCustomerNameOnOrderEntry: (next: boolean) => void;
  largeTableNumberOnReceipt?: boolean;
  onChangeLargeTableNumberOnReceipt?: (next: boolean) => void;
  stationWaiterSelectionEnabled?: boolean;
  onChangeStationWaiterSelectionEnabled?: (next: boolean) => void;
  cashCloseManagerOnly?: boolean;
  onChangeCashCloseManagerOnly?: (next: boolean) => void;
  openDrawerOnPartialCash?: boolean;
  onChangeOpenDrawerOnPartialCash?: (next: boolean) => void;
  openDrawerEmployeeIds?: number[];
  openDrawerEmployeeOptions?: DrawerEmployeeOption[];
  onChangeOpenDrawerEmployeeIds?: (next: number[]) => void;
  openDrawerRequiresPassword?: boolean;
  openDrawerPasswordConfigured?: boolean;
  onChangeOpenDrawerRequiresPassword?: (next: boolean) => void;
  onRequestOpenDrawerPasswordChange?: () => void;
  onRequestOpenDrawerPasswordReset?: () => void;
  employeeShiftCloseEnabled?: boolean;
  onChangeEmployeeShiftCloseEnabled?: (next: boolean) => void;
  zReportEmailRecipients?: string[];
  onSaveZReportEmailRecipients?: (emails: string[]) => void;
  refundCodeViaPush: boolean;
  refundCodeMode?: 'email' | 'push' | 'fixed';
  onChangeRefundCodeViaPush: (next: boolean) => void;
  onSetFixedRefundCode?: (code: string) => void;
  employeeAttendanceReportEnabled: boolean;
  onChangeEmployeeAttendanceReportEnabled: (next: boolean) => void;
  attendanceUiEnabled?: boolean;
  onChangeAttendanceUiEnabled?: (next: boolean) => void;
  rootClassName?: string;
};

export const PosSettingsModal: React.FC<PosSettingsModalProps> = ({
  opened,
  onClose,
  viewportWidth,
  isSubmitting,
  isSuperAdmin,
  requiresManagerApproval,
  onChangeRequiresManagerApproval,
  cancelRequiresManagerApproval = true,
  onChangeCancelRequiresManagerApproval = () => undefined,
  globalDiscountRequiresManagerApproval = true,
  onChangeGlobalDiscountRequiresManagerApproval = () => undefined,
  itemDiscountRequiresManagerApproval = true,
  onChangeItemDiscountRequiresManagerApproval = () => undefined,
  onTheHouseRequiresManagerApproval = true,
  onChangeOnTheHouseRequiresManagerApproval = () => undefined,
    transferRequiresManagerApproval = false,
    onChangeTransferRequiresManagerApproval = () => undefined,
    mergeRequiresManagerApproval = false,
    onChangeMergeRequiresManagerApproval = () => undefined,
    manualMergedTableSplitRequiresManagerApproval = false,
    onChangeManualMergedTableSplitRequiresManagerApproval = () => undefined,
  shiftCloseCashBalancesEnabled = false,
  onChangeShiftCloseCashBalancesEnabled = () => undefined,
  excludeOpeningCashFromDrawer = true,
  onChangeExcludeOpeningCashFromDrawer = () => undefined,
  ordersPageWaiterOwnVisibility,
  onChangeOrdersPageWaiterOwnVisibility,
  kitchenBonTableNumberAtBottom,
  onChangeKitchenBonTableNumberAtBottom,
  stayInOrderAfterFire,
  onChangeStayInOrderAfterFire,
  employeeIdleLogoutMinutes = 0,
  onChangeEmployeeIdleLogoutMinutes = () => undefined,
  warnNoBillBeforePayment = true,
  onChangeWarnNoBillBeforePayment = () => undefined,
  hideGeneralItemInMenu,
  onChangeHideGeneralItemInMenu,
  disableCashTips,
  onChangeDisableCashTips,
  waiterOwnOrderEntryOnly = false,
  onChangeWaiterOwnOrderEntryOnly = () => undefined,
  singleWorkerPerTable = false,
  onChangeSingleWorkerPerTable = () => undefined,
  manualCreditEntryBlocked,
  onChangeManualCreditEntryBlocked,
  customerNameOnOrderEntry,
  onChangeCustomerNameOnOrderEntry,
  largeTableNumberOnReceipt = false,
  onChangeLargeTableNumberOnReceipt = () => undefined,
  stationWaiterSelectionEnabled = false,
  onChangeStationWaiterSelectionEnabled = () => undefined,
  cashCloseManagerOnly = false,
  onChangeCashCloseManagerOnly = () => undefined,
  openDrawerOnPartialCash = false,
  onChangeOpenDrawerOnPartialCash = () => undefined,
  openDrawerEmployeeIds = [],
  openDrawerEmployeeOptions = [],
  onChangeOpenDrawerEmployeeIds = () => undefined,
  openDrawerRequiresPassword = false,
  openDrawerPasswordConfigured = false,
  onChangeOpenDrawerRequiresPassword = () => undefined,
  onRequestOpenDrawerPasswordChange = () => undefined,
  onRequestOpenDrawerPasswordReset = () => undefined,
  employeeShiftCloseEnabled = false,
  onChangeEmployeeShiftCloseEnabled = () => undefined,
  zReportEmailRecipients = [],
  onSaveZReportEmailRecipients,
  refundCodeViaPush,
  refundCodeMode = refundCodeViaPush ? 'push' : 'email',
  onChangeRefundCodeViaPush,
  onSetFixedRefundCode,
  employeeAttendanceReportEnabled,
  onChangeEmployeeAttendanceReportEnabled,
  attendanceUiEnabled = true,
  onChangeAttendanceUiEnabled = () => undefined,
  rootClassName,
}) => {
  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose();
  };

  const [drawerEmployeeSearch, setDrawerEmployeeSearch] = useState('');
  const [settingsSearch, setSettingsSearch] = useState('');
  const [fixedRefundSelected, setFixedRefundSelected] = useState(false);
  const [fixedRefundCode, setFixedRefundCode] = useState('');
  const [emailRecipientsDraft, setEmailRecipientsDraft] = useState<string[]>([]);
  const savedEmails = JSON.stringify(zReportEmailRecipients);
  useEffect(() => { setEmailRecipientsDraft(JSON.parse(savedEmails)); }, [opened, savedEmails]);
  const parsedEmails = zReportEmailRecipientsSchema.safeParse(emailRecipientsDraft);

  useEffect(() => {
    if (!opened) {
      setDrawerEmployeeSearch('');
      setSettingsSearch('');
      setFixedRefundSelected(false);
      setFixedRefundCode('');
    }
  }, [opened]);

  const selectedDrawerEmployeeIds = useMemo(
    () => Array.from(new Set(openDrawerEmployeeIds.filter((id) => Number.isInteger(id) && id > 0))),
    [openDrawerEmployeeIds],
  );

  const selectedWorkers = useMemo(
    () => selectedDrawerEmployeeIds
      .map((id) => {
        const found = openDrawerEmployeeOptions.find((option) => option.id === id);
        return found ? { id: found.id, fullName: found.fullName } : null;
      })
      .filter((employee): employee is DrawerEmployeeOption => Boolean(employee)),
    [openDrawerEmployeeOptions, selectedDrawerEmployeeIds],
  );

  const trimmedSearch = drawerEmployeeSearch.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!trimmedSearch) {
      return [] as DrawerEmployeeOption[];
    }
    return openDrawerEmployeeOptions
      .filter((option) => option.fullName.toLowerCase().includes(trimmedSearch))
      .slice(0, 25);
  }, [openDrawerEmployeeOptions, trimmedSearch]);

  const toggleDrawerEmployee = (employeeId: number) => {
    const current = selectedDrawerEmployeeIds;
    const next = current.includes(employeeId)
      ? current.filter((id) => id !== employeeId)
      : [...current, employeeId];
    onChangeOpenDrawerEmployeeIds(next);
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="הגדרות POS"
      closeButtonProps={{ 'data-testid': 'settings-pos-config-close', disabled: isSubmitting } as Record<string, unknown>}
      size={viewportWidth >= 768 ? 'clamp(760px, 78vw, 1120px)' : 'md'}
      centered={viewportWidth >= 768}
      radius="md"
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
      classNames={{
        root: rootClassName,
        content: classes.modalContent,
        body: classes.modalViewport,
      }}
    >
      <div className={classes.modalBody} dir="rtl">
        <p className={classes.lead}>הגדרות אלה נשמרות ברמת המסעדה.</p>
        <TextInput
          dir="rtl"
          className={classes.settingsSearch}
          value={settingsSearch}
          onChange={(event) => setSettingsSearch(event.currentTarget.value)}
          placeholder="חיפוש הגדרה"
          aria-label="חיפוש הגדרה"
          data-testid="pos-settings-search"
          leftSection={<FontAwesomeIcon icon={faMagnifyingGlass as IconProp} />}
        />
        <FilteredSettingsList query={settingsSearch.trim()}>
          <div className={classes.row}>
            <div className={classes.textBlock}>
              <span className={classes.label}>פעולות באישור מנהל</span>
              <span className={classes.hint}>סמן אילו פעולות דורשות אישור מנהל לפני ביצוע.</span>
              <div className={classes.approvalGrid}>
                {[
                  { key: 'general-item', label: 'פריט כללי', checked: requiresManagerApproval, onChange: onChangeRequiresManagerApproval },
                  { key: 'cancel', label: 'ביטול', checked: cancelRequiresManagerApproval, onChange: onChangeCancelRequiresManagerApproval },
                  { key: 'global-discount', label: 'הנחה כללית', checked: globalDiscountRequiresManagerApproval, onChange: onChangeGlobalDiscountRequiresManagerApproval },
                  { key: 'item-discount', label: 'הנחת פריט', checked: itemDiscountRequiresManagerApproval, onChange: onChangeItemDiscountRequiresManagerApproval },
                  { key: 'on-the-house', label: 'על חשבון הבית', checked: onTheHouseRequiresManagerApproval, onChange: onChangeOnTheHouseRequiresManagerApproval },
                  { key: 'transfer', label: 'העברה', checked: transferRequiresManagerApproval, onChange: onChangeTransferRequiresManagerApproval },
                  { key: 'merge', label: 'מיזוג', checked: mergeRequiresManagerApproval, onChange: onChangeMergeRequiresManagerApproval },
                  { key: 'split-merged-table', label: 'פיצול שולחן ממוזג', checked: manualMergedTableSplitRequiresManagerApproval, onChange: onChangeManualMergedTableSplitRequiresManagerApproval },
                ].map((item) => (
                  <label key={item.key} className={classes.approvalItem} htmlFor={`pos-setting-${item.key}-approval`}>
                    <Checkbox
                      id={`pos-setting-${item.key}-approval`}
                      checked={item.checked}
                      onChange={(event) => item.onChange(event.currentTarget.checked)}
                      disabled={isSubmitting}
                      data-testid={`pos-setting-${item.key}-approval`}
                    />
                    <span className={classes.approvalItemLabel}>{item.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <label className={classes.row} htmlFor="pos-setting-shift-close-cash-balances">
            <div className={classes.textBlock}>
              <span className={classes.label}>סגירת משמרת עם ספירת מזומן</span>
              <span className={classes.hint}>בסגירת Z ניתן להדפיס בלי לסגור, או להזין מזומן שנספר בקופה ומזומן פתיחה למשמרת הבאה.</span>
            </div>
            <Switch
              id="pos-setting-shift-close-cash-balances"
              checked={shiftCloseCashBalancesEnabled}
              onChange={(event) => onChangeShiftCloseCashBalancesEnabled(event.currentTarget.checked)}
              disabled={isSubmitting}
              data-testid="pos-setting-shift-close-cash-balances"
            />
          </label>
          <label className={classes.row} htmlFor="pos-setting-exclude-opening-cash-from-drawer">
            <div className={classes.textBlock}>
              <span className={classes.label}>ללא מזומן פתיחה במזומן במגירה</span>
              <span className={classes.hint}>כאשר פעיל, סכום מזומן הפתיחה אינו נכלל במזומן הצפוי בדוחות X ו-Z. ספירת המזומן אינה משתנה.</span>
            </div>
            <Switch
              id="pos-setting-exclude-opening-cash-from-drawer"
              checked={excludeOpeningCashFromDrawer}
              onChange={(event) => onChangeExcludeOpeningCashFromDrawer(event.currentTarget.checked)}
              disabled={isSubmitting}
              data-testid="pos-setting-exclude-opening-cash-from-drawer"
            />
          </label>
          <label className={classes.row} htmlFor="pos-setting-orders-page-waiter-own-visibility">
            <div className={classes.textBlock}>
              <span className={classes.label}>דף הזמנות: עובד רואה רק הזמנות שהוא פתח</span>
              <span className={classes.hint}>כאשר פעיל, עובדים שאינם מנהלים רואים ברשימת ההזמנות רק הזמנות שהאש הראשונה שלהן בוצעה על ידם.</span>
            </div>
            <Switch
              id="pos-setting-orders-page-waiter-own-visibility"
              checked={ordersPageWaiterOwnVisibility}
              onChange={(event) => {
                onChangeOrdersPageWaiterOwnVisibility(event.currentTarget.checked);
              }}
              disabled={isSubmitting}
              data-testid="pos-setting-orders-page-waiter-own-visibility"
            />
          </label>
          <label className={classes.row} htmlFor="pos-setting-kitchen-bon-table-number-at-bottom">
            <div className={classes.textBlock}>
              <span className={classes.label}>מספר שולחן בתחתית בון המטבח</span>
              <span className={classes.hint}>כאשר פעיל, מספר השולחן מודפס בתחתית הבון מתחת ללשבת/לקחת במקום בראשו.</span>
            </div>
            <Switch
              id="pos-setting-kitchen-bon-table-number-at-bottom"
              checked={kitchenBonTableNumberAtBottom}
              onChange={(event) => {
                onChangeKitchenBonTableNumberAtBottom(event.currentTarget.checked);
              }}
              disabled={isSubmitting}
              data-testid="pos-setting-kitchen-bon-table-number-at-bottom"
            />
          </label>
          <label className={classes.row} htmlFor="pos-setting-stay-in-order-after-fire">
            <div className={classes.textBlock}>
              <span className={classes.label}>הישאר בהזמנה לאחר אש</span>
              <span className={classes.hint}>כאשר פעיל, אש ירוקה או כתומה משאירה את המסך בהזמנה הנוכחית במקום לחזור לאזורים.</span>
            </div>
            <Switch
              id="pos-setting-stay-in-order-after-fire"
              checked={stayInOrderAfterFire}
              onChange={(event) => {
                onChangeStayInOrderAfterFire(event.currentTarget.checked);
              }}
              disabled={isSubmitting}
              data-testid="pos-setting-stay-in-order-after-fire"
            />
          </label>
          <label className={classes.row} htmlFor="pos-setting-employee-idle-logout-minutes">
            <div className={classes.textBlock}>
              <span className={classes.label}>נעילת מסך לאחר חוסר פעילות (דקות)</span>
              <span className={classes.hint}>כאשר המכשיר לא נוגע במשך הזמן שנבחר, העובד מוחזר למסך בחירת עובד. 0 = כבוי.</span>
            </div>
            <NumberInput
              id="pos-setting-employee-idle-logout-minutes"
              value={employeeIdleLogoutMinutes}
              min={0}
              max={240}
              step={1}
              clampBehavior="strict"
              allowDecimal={false}
              allowNegative={false}
              w={90}
              onChange={(value) => {
                const next = typeof value === 'number' ? value : Number(value);
                onChangeEmployeeIdleLogoutMinutes(Number.isFinite(next) ? Math.trunc(next) : 0);
              }}
              disabled={isSubmitting}
              data-testid="pos-setting-employee-idle-logout-minutes"
            />
          </label>
          <label className={classes.row} htmlFor="pos-setting-warn-no-bill-before-payment">
            <div className={classes.textBlock}>
              <span className={classes.label}>אזהרה על תשלום ללא הדפסת חשבון</span>
              <span className={classes.hint}>כאשר פעיל, כניסה למסך התשלום בהזמנת ישיבה שלא הודפס לה חשבון מציגה התראה. לא חל על לקחת והזמנה מהירה.</span>
            </div>
            <Switch
              id="pos-setting-warn-no-bill-before-payment"
              checked={warnNoBillBeforePayment}
              onChange={(event) => onChangeWarnNoBillBeforePayment(event.currentTarget.checked)}
              disabled={isSubmitting}
              data-testid="pos-setting-warn-no-bill-before-payment"
            />
          </label>
          <label className={classes.row} htmlFor="pos-setting-hide-general-item-in-menu">
            <div className={classes.textBlock}>
              <span className={classes.label}>הסתר פריט כללי מהתפריט</span>
              <span className={classes.hint}>כאשר פעיל, אריח הפריט הכללי אינו מוצג בתפריט המוצרים. הפריט הכללי נשאר זמין במסך פעולות.</span>
            </div>
            <Switch
              id="pos-setting-hide-general-item-in-menu"
              checked={hideGeneralItemInMenu}
              onChange={(event) => {
                onChangeHideGeneralItemInMenu(event.currentTarget.checked);
              }}
              disabled={isSubmitting}
              data-testid="pos-setting-hide-general-item-in-menu"
            />
          </label>
          <label className={classes.row} htmlFor="pos-setting-disable-cash-tips">
            <div className={classes.textBlock}>
              <span className={classes.label}>ביטול טיפ במזומן</span>
              <span className={classes.hint}>כאשר פעיל, אפשרויות הטיפ במזומן מוסתרות, ובסגירת ההזמנה טיפים שנגבו במזומן נמחקים ונשמר רק סכום הבסיס.</span>
            </div>
            <Switch
              id="pos-setting-disable-cash-tips"
              checked={disableCashTips}
              onChange={(event) => {
                onChangeDisableCashTips(event.currentTarget.checked);
              }}
              disabled={isSubmitting}
              data-testid="pos-setting-disable-cash-tips"
            />
          </label>
          <label className={classes.row} htmlFor="pos-setting-waiter-own-order-entry-only">
            <div className={classes.textBlock}>
              <span className={classes.label}>כניסה להזמנה: עובד נכנס רק להזמנות שהוא פתח</span>
              <span className={classes.hint}>כאשר פעיל, עובד שאינו מנהל יכול לראות את כל האזורים אך אינו יכול להיכנס להזמנה של עובד אחר.</span>
            </div>
            <Switch
              id="pos-setting-waiter-own-order-entry-only"
              checked={waiterOwnOrderEntryOnly}
              onChange={(event) => {
                onChangeWaiterOwnOrderEntryOnly(event.currentTarget.checked);
              }}
              disabled={isSubmitting}
              data-testid="pos-setting-waiter-own-order-entry-only"
            />
          </label>
          <label className={classes.row} htmlFor="pos-setting-single-worker-per-table">
            <div className={classes.textBlock}>
              <span className={classes.label}>עובד אחד בכל שולחן</span>
              <span className={classes.hint}>כאשר פעיל, רק עובד אחד יכול להיות בתוך הזמנת שולחן בכל זמן.</span>
            </div>
            <Switch
              id="pos-setting-single-worker-per-table"
              checked={singleWorkerPerTable}
              onChange={(event) => {
                onChangeSingleWorkerPerTable(event.currentTarget.checked);
              }}
              disabled={isSubmitting}
              data-testid="pos-setting-single-worker-per-table"
            />
          </label>
          <label className={classes.row} htmlFor="pos-setting-manual-credit-entry-blocked">
            <div className={classes.textBlock}>
              <span className={classes.label}>חסימת אשראי ידני</span>
              <span className={classes.hint}>כאשר פעיל, אפשרות הזנת פרטי כרטיס ידנית מוסתרת במסך התשלום.</span>
            </div>
            <Switch
              id="pos-setting-manual-credit-entry-blocked"
              checked={manualCreditEntryBlocked}
              onChange={(event) => {
                onChangeManualCreditEntryBlocked(event.currentTarget.checked);
              }}
              disabled={isSubmitting}
              data-testid="pos-setting-manual-credit-entry-blocked"
            />
          </label>
          <label className={classes.row} htmlFor="pos-setting-customer-name-on-order-entry">
            <div className={classes.textBlock}>
              <span className={classes.label}>שם לקוח בכניסה להזמנה</span>
              <span className={classes.hint}>כאשר פעיל, בפתיחת שולחן חדש או הזמנת לקחת תוצג בקשת שם לקוח (בטאבלט בלבד).</span>
            </div>
            <Switch
              id="pos-setting-customer-name-on-order-entry"
              checked={customerNameOnOrderEntry}
              onChange={(event) => {
                onChangeCustomerNameOnOrderEntry(event.currentTarget.checked);
              }}
              disabled={isSubmitting}
              data-testid="pos-setting-customer-name-on-order-entry"
            />
          </label>
          <label className={classes.row} htmlFor="pos-setting-large-table-number-on-receipt">
            <div className={classes.textBlock}>
              <span className={classes.label}>מספר שולחן ענק בקבלה</span>
              <span className={classes.hint}>כאשר פעיל, הדפסת חשבון מציגה את מספר השולחן מעל כותרת החשבון.</span>
            </div>
            <Switch
              id="pos-setting-large-table-number-on-receipt"
              checked={largeTableNumberOnReceipt}
              onChange={(event) => {
                onChangeLargeTableNumberOnReceipt(event.currentTarget.checked);
              }}
              disabled={isSubmitting}
              data-testid="pos-setting-large-table-number-on-receipt"
            />
          </label>
          <label className={classes.row} htmlFor="pos-setting-station-waiter-selection">
            <div className={classes.textBlock}>
              <span className={classes.label}>בחירת מלצר בעמדה נייחת</span>
              <span className={classes.hint}>כאשר פעיל, בכניסה לשולחן או בפתיחת הזמנה נבחר המלצר המבצע, והפעולות (אש, תשלום) נרשמות על שמו (לא במצב טלפון).</span>
            </div>
            <Switch
              id="pos-setting-station-waiter-selection"
              checked={stationWaiterSelectionEnabled}
              onChange={(event) => {
                onChangeStationWaiterSelectionEnabled(event.currentTarget.checked);
              }}
              disabled={isSubmitting}
              data-testid="pos-setting-station-waiter-selection"
            />
          </label>
          <label className={classes.row} htmlFor="pos-setting-cash-close-manager-only">
            <div className={classes.textBlock}>
              <span className={classes.label}>סגירת הזמנה במזומן למנהל בלבד</span>
              <span className={classes.hint}>כאשר פעיל, רק מנהל יכול לסגור תשלום במזומן; לעובדים אפשרות המזומן מוסתרת והשרת חוסם אותה.</span>
            </div>
            <Switch
              id="pos-setting-cash-close-manager-only"
              checked={cashCloseManagerOnly}
              onChange={(event) => {
                onChangeCashCloseManagerOnly(event.currentTarget.checked);
              }}
              disabled={isSubmitting}
              data-testid="pos-setting-cash-close-manager-only"
            />
          </label>
          <label className={classes.row} htmlFor="pos-setting-open-drawer-on-partial-cash">
            <div className={classes.textBlock}>
              <span className={classes.label}>פתח מגירה גם בתשלום חלקי</span>
              <span className={classes.hint}>כאשר פעיל, בכל תשלום מזומן המגירה תיפתח גם אם ההזמנה נשארת פתוחה.</span>
            </div>
            <Switch
              id="pos-setting-open-drawer-on-partial-cash"
              checked={openDrawerOnPartialCash}
              onChange={(event) => {
                onChangeOpenDrawerOnPartialCash(event.currentTarget.checked);
              }}
              disabled={isSubmitting}
              data-testid="pos-setting-open-drawer-on-partial-cash"
            />
          </label>
          <div className={classes.rowStack}>
            <div className={classes.textBlock}>
              <span className={classes.label}>בחירת עובדים לפתיחת מגירה</span>
              <span className={classes.hint}>הקלד שם עובד (לא מנהל) ובחר מי יוכל לראות את אפשרות פתח מגירה בהמבורגר.</span>
            </div>
            <TextInput
              className={classes.workerSearchInput}
              data-testid="pos-setting-open-drawer-workers-search"
              placeholder="חיפוש עובד..."
              value={drawerEmployeeSearch}
              onChange={(event) => setDrawerEmployeeSearch(event.currentTarget.value)}
              onContextMenu={(event) => event.preventDefault()}
              onSelect={(event) => {
                const input = event.currentTarget;
                const end = input.value.length;
                if (input.selectionStart !== end || input.selectionEnd !== end) {
                  input.setSelectionRange(end, end);
                }
              }}
              disabled={isSubmitting}
              aria-label="חיפוש עובדים לפתיחת מגירה"
            />
            {selectedWorkers.length > 0 && (
              <div className={classes.selectedBadges} data-testid="pos-setting-open-drawer-workers-selected">
                {selectedWorkers.map((worker) => (
                  <Badge key={worker.id} variant="light" color="green">
                    {worker.fullName}
                  </Badge>
                ))}
              </div>
            )}
            {trimmedSearch && (
              <div className={classes.searchResults} data-testid="pos-setting-open-drawer-workers-results">
                {searchResults.length === 0 ? (
                  <div className={classes.searchEmpty}>לא נמצאו עובדים</div>
                ) : (
                  searchResults.map((option) => {
                    const selected = selectedDrawerEmployeeIds.includes(option.id);
                    return (
                      <label key={option.id} className={classes.resultRow} htmlFor={`drawer-worker-${option.id}`}>
                        <span>{option.fullName}</span>
                        <Checkbox
                          id={`drawer-worker-${option.id}`}
                          checked={selected}
                          disabled={isSubmitting}
                          onChange={() => toggleDrawerEmployee(option.id)}
                          data-testid={`pos-setting-open-drawer-worker-${option.id}`}
                        />
                      </label>
                    );
                  })
                )}
              </div>
            )}
          </div>
          <div className={classes.rowStack}>
            <label className={classes.rowBare} htmlFor="pos-setting-open-drawer-requires-password">
              <div className={classes.textBlock}>
                <span className={classes.titleRow}>
                  <span className={classes.label}>פתיחת מגירה דורשת סיסמה</span>
                  {openDrawerPasswordConfigured && (
                    <span className={classes.configuredMark} data-testid="pos-setting-open-drawer-password-configured">
                      פעיל
                    </span>
                  )}
                </span>
                <span className={classes.hint}>כאשר פעיל, לחיצה על פתח מגירה בהמבורגר תחייב הזנת סיסמה לפני הפתיחה.</span>
              </div>
              <div className={classes.drawerPasswordActions}>
                {openDrawerPasswordConfigured && isSuperAdmin && openDrawerRequiresPassword && (
                  <button
                    type="button"
                    className={classes.resetAction}
                    onClick={() => {
                      if (isSubmitting) {
                        return;
                      }
                      onRequestOpenDrawerPasswordReset();
                    }}
                    disabled={isSubmitting}
                    data-testid="pos-setting-open-drawer-password-reset"
                  >
                    איפוס סיסמה
                  </button>
                )}
                {openDrawerPasswordConfigured && openDrawerRequiresPassword && (
                  <button
                    type="button"
                    className={classes.resetAction}
                    onClick={() => {
                      if (isSubmitting) {
                        return;
                      }
                      onRequestOpenDrawerPasswordChange();
                    }}
                    disabled={isSubmitting}
                    data-testid="pos-setting-open-drawer-password-change"
                  >
                    שינוי סיסמה
                  </button>
                )}
                <Switch
                  id="pos-setting-open-drawer-requires-password"
                  checked={openDrawerRequiresPassword}
                  onChange={(event) => {
                    onChangeOpenDrawerRequiresPassword(event.currentTarget.checked);
                  }}
                  disabled={isSubmitting}
                  data-testid="pos-setting-open-drawer-requires-password"
                />
              </div>
            </label>
          </div>
          <label className={classes.row} htmlFor="pos-setting-employee-shift-close-enabled">
            <div className={classes.textBlock}>
              <span className={classes.label}>סגירת יום על ידי עובד</span>
              <span className={classes.hint}>כאשר פעיל, עובד שאינו מנהל יכול לראות את כפתור Z סוף יום בהמבורגר ולסגור משמרת.</span>
            </div>
            <Switch
              id="pos-setting-employee-shift-close-enabled"
              checked={employeeShiftCloseEnabled}
              onChange={(event) => onChangeEmployeeShiftCloseEnabled(event.currentTarget.checked)}
              disabled={isSubmitting}
              data-testid="pos-setting-employee-shift-close-enabled"
            />
          </label>
          <div className={classes.row}>
            <div className={`${classes.textBlock} ${classes.emailField}`}>
              <QuickNotesField label="שליחת דוח Z במייל בסגירת משמרת" addLabel="הוסף כתובת מייל"
                value={emailRecipientsDraft} onChange={setEmailRecipientsDraft} disabled={isSubmitting}
                parseValues={(emails) => {
                  const parsed = zReportEmailRecipientsSchema.safeParse(emails);
                  return parsed.success ? parsed.data : null;
                }}
                inputProps={{ dir: 'ltr', inputMode: 'email', maxLength: 254, 'aria-label': 'כתובות מייל לדוח Z', placeholder: '',
                  styles: { input: { userSelect: 'none', WebkitTouchCallout: 'none' } },
                  onContextMenu: (event) => event.preventDefault(),
                  onSelect: (event) => { const input = event.currentTarget; input.setSelectionRange(input.value.length, input.value.length); },
                }} />
            </div>
            <Button disabled={isSubmitting || !parsedEmails.success || !onSaveZReportEmailRecipients
              || (parsedEmails.success && JSON.stringify(parsedEmails.data) === JSON.stringify(zReportEmailRecipients))}
              leftSection={<FontAwesomeIcon icon={faFloppyDisk as IconProp} />}
              onClick={() => { if (parsedEmails.success) onSaveZReportEmailRecipients?.(parsedEmails.data); }}>
              שמור
            </Button>
          </div>
          <label className={classes.row} htmlFor="pos-setting-employee-attendance-report-enabled">
            <div className={classes.textBlock}>
              <span className={classes.label}>דוח נוכחות עצמי לעובד</span>
              <span className={classes.hint}>כאשר פעיל, עובד שאינו מנהל יכול לפתוח מההמבורגר דוח נוכחות אישי בלבד.</span>
            </div>
            <Switch
              id="pos-setting-employee-attendance-report-enabled"
              checked={employeeAttendanceReportEnabled}
              onChange={(event) => {
                onChangeEmployeeAttendanceReportEnabled(event.currentTarget.checked);
              }}
              disabled={isSubmitting}
              data-testid="pos-setting-employee-attendance-report-enabled"
            />
          </label>
          <label className={classes.row} htmlFor="pos-setting-attendance-ui-enabled">
            <div className={classes.textBlock}>
              <span className={classes.label}>הצגת נוכחות ב-POS</span>
              <span className={classes.hint}>מציג או מסתיר כניסה למשמרת ובקרי נוכחות בתפריט ההמבורגר.</span>
            </div>
            <Switch id="pos-setting-attendance-ui-enabled" checked={attendanceUiEnabled} onChange={(event) => onChangeAttendanceUiEnabled(event.currentTarget.checked)} disabled={isSubmitting} data-testid="pos-setting-attendance-ui-enabled" />
          </label>
          {isSuperAdmin && (
            <div className={classes.row}>
              <div className={classes.textBlock}>
                <span className={classes.label}>אימות זיכוי</span>
                <span className={classes.hint}>קוד חד-פעמי במייל או בהתראת PWA, או קוד קבוע להגדרה.</span>
              </div>
              <div>
                <Select aria-label="אופן אימות זיכוי" data-testid="pos-setting-refund-code-mode"
                  data={[{ value: 'email', label: 'מייל' }, { value: 'push', label: 'PWA' },
                    { value: 'fixed', label: 'קוד קבוע' }]}
                  value={fixedRefundSelected ? 'fixed' : refundCodeMode} disabled={isSubmitting}
                  onChange={(value) => {
                    if (value === 'fixed') setFixedRefundSelected(true);
                    else if (value === 'email' || value === 'push') {
                      setFixedRefundSelected(false);
                      onChangeRefundCodeViaPush(value === 'push');
                    }
                  }} />
                {(fixedRefundSelected || refundCodeMode === 'fixed') && <>
                  <TextInput type="password" label="קוד זיכוי קבוע" inputMode="numeric" maxLength={6}
                    value={fixedRefundCode} onChange={(event) => setFixedRefundCode(event.currentTarget.value.replace(/\D/g, ''))}
                    onContextMenu={(event) => event.preventDefault()}
                    onSelect={(event) => { const input = event.currentTarget; input.setSelectionRange(input.value.length, input.value.length); }} />
                  <Button disabled={isSubmitting || fixedRefundCode.length !== 6}
                    onClick={() => { onSetFixedRefundCode?.(fixedRefundCode); setFixedRefundCode(''); setFixedRefundSelected(false); }}>
                    שמור קוד
                  </Button>
                </>}
              </div>
            </div>
          )}
        </FilteredSettingsList>
      </div>
    </Modal>
  );
};
